import type {
  DiaryDayBody,
  DiaryDayEntry,
  DiaryGoalFlexMacro,
  DiaryMeal,
  DiaryMealPlanKind,
  DiaryMealPlanMacroKey,
  DiaryMealPeriodization,
} from "./diaryDays";
import type { Meal } from "@macro-tracker/models";
import {
  configuredMealPlanId,
  resolveMealTarget,
  resolveMealExercise,
  resolveMealPlannedAt,
} from "@macro-tracker/models";
import { resolveDailyGoals } from "@/utils/diaryDayTotals";
import { toLocalDateInput, formatDateInput, formatTimeInput, toUtcIsoFromLocalDateTime } from "@/utils/dateTime";

export interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

type MacroTargetWithFlex = MacroTotals & {
  flexMacro?: DiaryGoalFlexMacro | null;
};

const MACRO_CALORIES_PER_GRAM = {
  protein: 4,
  carbs: 4,
  fat: 9,
} as const;

export const MACRO_KEYS: DiaryMealPlanMacroKey[] = [
  "calories",
  "protein",
  "carbs",
  "fat",
];

const emptyTotals = (): MacroTotals => ({
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
});

const isFlexMacro = (value: unknown): value is DiaryGoalFlexMacro =>
  value === "carbs" || value === "fat";

const normalizeFlexTarget = (target: MacroTargetWithFlex): MacroTargetWithFlex => {
  if (!isFlexMacro(target.flexMacro)) return target;

  if (target.flexMacro === "carbs") {
    const fixedCalories =
      target.protein * MACRO_CALORIES_PER_GRAM.protein +
      target.fat * MACRO_CALORIES_PER_GRAM.fat;
    return {
      ...target,
      carbs: Math.max(0, (target.calories - fixedCalories) / MACRO_CALORIES_PER_GRAM.carbs),
    };
  }

  const fixedCalories =
    target.protein * MACRO_CALORIES_PER_GRAM.protein +
    target.carbs * MACRO_CALORIES_PER_GRAM.carbs;
  return {
    ...target,
    fat: Math.max(0, (target.calories - fixedCalories) / MACRO_CALORIES_PER_GRAM.fat),
  };
};

const subtractTotals = (target: MacroTargetWithFlex, reserved: MacroTotals): MacroTargetWithFlex => ({
  calories: Math.max(0, target.calories - reserved.calories),
  protein: Math.max(0, target.protein - reserved.protein),
  carbs: Math.max(0, target.carbs - reserved.carbs),
  fat: Math.max(0, target.fat - reserved.fat),
  ...(target.flexMacro ? { flexMacro: target.flexMacro } : {}),
});

const addTotals = (a: MacroTotals, b: MacroTotals): MacroTotals => ({
  calories: a.calories + b.calories,
  protein: a.protein + b.protein,
  carbs: a.carbs + b.carbs,
  fat: a.fat + b.fat,
});

const roundTotals = (totals: MacroTotals): MacroTotals => ({
  calories: Math.round(Math.max(0, totals.calories)),
  protein: Math.round(Math.max(0, totals.protein)),
  carbs: Math.round(Math.max(0, totals.carbs)),
  fat: Math.round(Math.max(0, totals.fat)),
});

const allocateRoundedBudget = (budget: number, slotCount: number): number[] => {
  if (slotCount <= 0) return [];
  const safeBudget = Math.max(0, budget);
  return allocateRoundedValues(
    Array.from({ length: slotCount }, () => safeBudget / slotCount),
  );
};

const allocateRoundedValues = (values: number[]): number[] => {
  const allocations = values.map((value, index) => {
    const safeValue = Math.max(0, value);
    const floored = Math.floor(safeValue);
    return {
      index,
      value: floored,
      remainder: safeValue - floored,
    };
  });
  let remaining = Math.max(
    0,
    Math.round(values.reduce((sum, value) => sum + Math.max(0, value), 0)) -
      allocations.reduce((sum, item) => sum + item.value, 0),
  );
  const order = [...allocations].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index,
  );
  for (const item of order) {
    if (remaining <= 0) break;
    allocations[item.index].value += 1;
    remaining -= 1;
  }
  return allocations.map((item) => item.value);
};

const allocateFlexMacroValues = (
  flexCalories: number[],
  flexMacro: DiaryGoalFlexMacro,
): number[] =>
  allocateRoundedValues(
    flexCalories.map((calories) => flexMacroGrams(calories, flexMacro)),
  );

const flexMacroGrams = (
  calories: number,
  flexMacro: DiaryGoalFlexMacro,
): number => Math.max(0, calories / MACRO_CALORIES_PER_GRAM[flexMacro]);

export const splitRemainingTargets = (
  dailyTarget: MacroTargetWithFlex,
  reservedTarget: MacroTotals,
  slotCount: number,
): MacroTotals[] => {
  if (slotCount <= 0) return [];

  const remaining = subtractTotals(normalizeFlexTarget(dailyTarget), reservedTarget);
  const calories = allocateRoundedBudget(remaining.calories, slotCount);
  const protein = allocateRoundedBudget(remaining.protein, slotCount);

  if (remaining.flexMacro === "carbs") {
    const fat = allocateRoundedBudget(remaining.fat, slotCount);
    const carbs = allocateFlexMacroValues(
      calories.map(
        (mealCalories, index) =>
          mealCalories -
          protein[index] * MACRO_CALORIES_PER_GRAM.protein -
          fat[index] * MACRO_CALORIES_PER_GRAM.fat,
      ),
      "carbs",
    );
    return calories.map((mealCalories, index) => ({
      calories: mealCalories,
      protein: protein[index],
      carbs: carbs[index],
      fat: fat[index],
    }));
  }

  if (remaining.flexMacro === "fat") {
    const carbs = allocateRoundedBudget(remaining.carbs, slotCount);
    const fat = allocateFlexMacroValues(
      calories.map(
        (mealCalories, index) =>
          mealCalories -
          protein[index] * MACRO_CALORIES_PER_GRAM.protein -
          carbs[index] * MACRO_CALORIES_PER_GRAM.carbs,
      ),
      "fat",
    );
    return calories.map((mealCalories, index) => ({
      calories: mealCalories,
      protein: protein[index],
      carbs: carbs[index],
      fat: fat[index],
    }));
  }

  const carbs = allocateRoundedBudget(remaining.carbs, slotCount);
  const fat = allocateRoundedBudget(remaining.fat, slotCount);
  return calories.map((mealCalories, index) => ({
    calories: mealCalories,
    protein: protein[index],
    carbs: carbs[index],
    fat: fat[index],
  }));
};

const equalWeights = (): Record<DiaryMealPlanMacroKey, number> => ({
  calories: 0.25,
  protein: 0.25,
  carbs: 0.25,
  fat: 0.25,
});

export const defaultPeriodization = (): DiaryMealPeriodization => ({
  strategy: "equal",
  mode: "simple",
  simpleCarbLevel: "medium",
  generatedWeights: equalWeights(),
  macroRatios: { calories: 100, protein: 30, carbs: 40, fat: 30 },
});

/** Resolve the effective kind for a meal, inferring it if absent. */
export const resolvePlanKind = (plan: DiaryMeal): DiaryMealPlanKind => {
  if (plan.kind) return plan.kind;
  const target = resolveMealTarget(plan);
  const hasTarget = target !== null &&
    (target.calories > 0 || target.protein > 0 || target.carbs > 0 || target.fat > 0);
  const exercise = resolveMealExercise(plan);
  const hasExercise =
    exercise !== null &&
    ((exercise.caloriesBurned ?? 0) > 0 ||
      (exercise.durationMinutes ?? 0) > 0);
  if (hasExercise && hasTarget) return "meal-with-workout";
  if (hasExercise) return "workout";
  return "meal";
};

/** Resolve effective periodization metadata, falling back to defaults. */
export const resolvePeriodization = (
  plan: DiaryMeal,
): DiaryMealPeriodization => plan.periodization ?? defaultPeriodization();

const isExerciseEntry = (entry: DiaryDayEntry): boolean =>
  entry.source.type === "exercise";

/** Sum food entry macros grouped by meal id. Excludes exercise entries. */
const computeFoodTotalsByMealPlan = (
  body: DiaryDayBody,
): Map<string, MacroTotals> => {
  const totals = new Map<string, MacroTotals>();
  for (const meal of body.meals) {
    if (meal.deletedAt) continue;
    let cur = emptyTotals();
    for (const entry of meal.entries) {
      if (isExerciseEntry(entry)) continue;
      cur = {
        calories: cur.calories + (entry.nutritionSnapshot.calories ?? 0),
        protein: cur.protein + (entry.nutritionSnapshot.protein ?? 0),
        carbs: cur.carbs + (entry.nutritionSnapshot.carbs ?? 0),
        fat: cur.fat + (entry.nutritionSnapshot.fat ?? 0),
      };
    }
    totals.set(meal.id, cur);
  }
  return totals;
};

interface ResolvedWorkoutContribution {
  caloriesBurned: number;
  fuelCalories: number;
}

/** Sum workout contribution: actual when present, else planned for unlogged. */
const resolveWorkoutContribution = (
  body: DiaryDayBody,
): ResolvedWorkoutContribution => {
  let burned = 0;
  let fuel = 0;

  // Sum exercise entries directly (these are the source-of-truth actuals).
  for (const meal of body.meals) {
    if (meal.deletedAt) continue;
    for (const entry of meal.entries) {
      if (!isExerciseEntry(entry)) continue;
      burned += entry.nutritionSnapshot.calories ?? 0;
    }
  }

  for (const plan of body.meals) {
    if (plan.deletedAt) continue;
    const kind = resolvePlanKind(plan);
    if (kind === "meal") continue;
    // A linked exercise diary entry (inside this meal) is the canonical
    // completion record and has already been counted above — skip meal-level values.
    const hasLinkedEntry = plan.entries.some(isExerciseEntry);
    if (hasLinkedEntry) continue;
    if (plan.workoutActuals) {
      // Actual values authoritative; replace any planned contribution.
      burned += plan.workoutActuals.caloriesBurned;
      fuel += plan.workoutActuals.fuelCalories;
      continue;
    }
    // Use planned values only when no actuals exist.
    const exercise = resolveMealExercise(plan);
    if (exercise) {
      burned += exercise.caloriesBurned ?? 0;
      fuel += exercise.caloriesConsumed ?? 0;
    }
  }

  return { caloriesBurned: burned, fuelCalories: fuel };
};

interface PlanEligibility {
  plan: DiaryMeal;
  kind: DiaryMealPlanKind;
  isCompleted: boolean;
  isSkipped: boolean;
  isDeleted: boolean;
  isPast: boolean;
}

const isLocalToday = (selectedDate: string, now: Date): boolean =>
  formatDateInput(now) === selectedDate;

const isPlanInPast = (
  plan: DiaryMeal,
  selectedDate: string,
  now: Date,
): boolean => {
  // For a future-dated diary day, no plans are "past".
  if (!isLocalToday(selectedDate, now)) return false;
  const plannedAt = resolveMealPlannedAt(plan);
  const localDate = toLocalDateInput(plannedAt);
  if (localDate && localDate < selectedDate) return true;
  if (localDate && localDate > selectedDate) return false;
  // Same local date — compare time-of-day.
  const plannedTime = (plannedAt && plannedAt.length >= 16)
    ? new Date(plannedAt).getTime()
    : Number.NaN;
  if (!Number.isFinite(plannedTime)) return false;
  return plannedTime < now.getTime();
};

const classifyPlan = (
  plan: DiaryMeal,
  selectedDate: string,
  now: Date,
): PlanEligibility => {
  return {
    plan,
    kind: resolvePlanKind(plan),
    isCompleted: !!plan.completedAt,
    isSkipped: !!plan.skippedAt,
    isDeleted: !!plan.deletedAt,
    isPast: isPlanInPast(plan, selectedDate, now),
  };
};

const isEligible = (e: PlanEligibility, selectedDate: string, now: Date): boolean => {
  if (e.kind !== "meal") return false;
  if (e.isDeleted || e.isCompleted || e.isSkipped) return false;
  // Past selected date → nothing is eligible.
  const today = formatDateInput(now);
  if (selectedDate < today) return false;
  // Today → only future plans eligible.
  if (selectedDate === today && e.isPast) return false;
  return true;
};

export interface PeriodizationInput {
  body: DiaryDayBody;
  selectedDate: string;
  meals?: Meal[];
  now?: Date;
}

export interface PeriodizationOutput {
  mealPlans: DiaryMeal[];
  changedPlanIds: string[];
  message: string | null;
}

/**
 * Pure periodization service. Distributes remaining macros equally across
 * eligible meal plans and persists generated weights. Does not mutate input.
 */
export const recalculateMealPlans = (
  input: PeriodizationInput,
): PeriodizationOutput => {
  const { body, selectedDate } = input;
  const now = input.now ?? new Date();
  const plans = [...body.meals];

  // Auto-create missing meal plans for enabled configured meals
  if (input.meals && input.meals.length > 0) {
    const mealsWithTime = input.meals.filter(
      (m): m is typeof m & { startTime: string } => Boolean(m.enabled && m.startTime),
    );

    for (const meal of mealsWithTime) {
      const existingPlan = plans.find(
        p => p.mealId === meal.id && resolvePlanKind(p) === 'meal'
      );

      if (!existingPlan) {
        const newPlan: DiaryMeal = {
          id: configuredMealPlanId(selectedDate, meal.id),
          mealId: meal.id,
          kind: 'meal',
          planned: {
            title: meal.title,
            plannedAt: toUtcIsoFromLocalDateTime(selectedDate, meal.startTime),
            target: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            exercise: null,
          },
          entries: [],
          periodization: {
            strategy: 'equal',
            mode: 'simple',
            simpleCarbLevel: null,
            generatedWeights: { calories: 1, protein: 1, carbs: 1, fat: 1 },
            macroRatios: { calories: 2000, protein: 150, carbs: 200, fat: 70 },
          },
        };
        plans.push(newPlan);
      }
    }
  }

  const baseGoals = resolveDailyGoals(body);
  const foodTotals = computeFoodTotalsByMealPlan(body);
  const workout = resolveWorkoutContribution(body);

  // Workout burn increases the day's total intake target. During-workout fuel
  // counts as consumed intake against that target, so the meal allocation gets
  // the remaining calories after planned/actual workout fuel.
  if (!baseGoals) {
    return { mealPlans: plans, changedPlanIds: [], message: null };
  }
  const adjustedDay: MacroTargetWithFlex = {
    calories: baseGoals.calories + workout.caloriesBurned - workout.fuelCalories,
    protein: baseGoals.protein,
    carbs: baseGoals.carbs,
    fat: baseGoals.fat,
    ...(baseGoals.flexMacro ? { flexMacro: baseGoals.flexMacro } : {}),
  };

  // Subtract fixed completed/skipped/past totals, then treat food on eligible
  // meals as a floor that can still receive additional allocation.
  const classified = plans.map((p) => classifyPlan(p, selectedDate, now));
  const consumedFromLocked: MacroTotals = emptyTotals();
  for (const e of classified) {
    if (e.isDeleted) continue;
    const { plan } = e;
    // Completed/skipped/past meals — their target reflects the lock.
    if (e.isCompleted || e.isSkipped || (e.isPast && e.kind === "meal")) {
      const locked = resolveMealTarget(plan) ?? emptyTotals();
      consumedFromLocked.calories += locked.calories;
      consumedFromLocked.protein += locked.protein;
      consumedFromLocked.carbs += locked.carbs;
      consumedFromLocked.fat += locked.fat;
      continue;
    }
  }

  const eligible = classified.filter((e) => isEligible(e, selectedDate, now));
  if (eligible.length === 0) {
    return { mealPlans: plans, changedPlanIds: [], message: null };
  }
  const eligibleMinimumByPlanId = new Map<string, MacroTotals>();
  const eligibleMinimumTotals = emptyTotals();
  for (const e of eligible) {
    const minimum = roundTotals(foodTotals.get(e.plan.id) ?? emptyTotals());
    if (
      minimum.calories === 0 &&
      minimum.protein === 0 &&
      minimum.carbs === 0 &&
      minimum.fat === 0
    ) {
      continue;
    }
    eligibleMinimumByPlanId.set(e.plan.id, minimum);
    eligibleMinimumTotals.calories += minimum.calories;
    eligibleMinimumTotals.protein += minimum.protein;
    eligibleMinimumTotals.carbs += minimum.carbs;
    eligibleMinimumTotals.fat += minimum.fat;
  }

  const share = 1 / eligible.length;
  const weights: Record<DiaryMealPlanMacroKey, number> = {
    calories: share,
    protein: share,
    carbs: share,
    fat: share,
  };
  const allocatedTargets = splitRemainingTargets(
    adjustedDay,
    addTotals(consumedFromLocked, eligibleMinimumTotals),
    eligible.length,
  );
  const allocatedTargetByPlanId = new Map(
    eligible.map((item, index) => [item.plan.id, allocatedTargets[index]]),
  );

  const changedIds: string[] = [];
  const nextPlans: DiaryMeal[] = plans.map((plan) => {
    const e = classified.find((c) => c.plan.id === plan.id)!;
    if (!isEligible(e, selectedDate, now)) {
      return plan;
    }
    const minimum = eligibleMinimumByPlanId.get(plan.id) ?? emptyTotals();
    const target = addTotals(minimum, allocatedTargetByPlanId.get(plan.id) ?? emptyTotals());
    const nextPeriodization: DiaryMealPeriodization = {
      ...resolvePeriodization(plan),
      generatedWeights: weights,
      strategy: "equal",
    };
    const prev = plan.planned.target;
    const targetChanged =
      !prev ||
      target.calories !== prev.calories ||
      target.protein !== prev.protein ||
      target.carbs !== prev.carbs ||
      target.fat !== prev.fat;
    if (targetChanged) changedIds.push(plan.id);
    return {
      ...plan,
      planned: { ...plan.planned, target },
      periodization: nextPeriodization,
    };
  });

  return {
    mealPlans: nextPlans,
    changedPlanIds: changedIds,
    message: changedIds.length > 0 ? "Targets recalculated" : null,
  };
};

/**
 * Complete a meal plan: lock its target to actual food totals, set
 * completedAt (or skippedAt for empty meals), then run recalculation.
 */
export const completePlan = (
  body: DiaryDayBody,
  planId: string,
  options: { selectedDate: string; now?: Date },
): { mealPlans: DiaryMeal[]; changedPlanIds: string[]; message: string | null } => {
  const now = options.now ?? new Date();
  const planToComplete = body.meals.find((plan) => plan.id === planId) ?? null;
  const completedAt =
    planToComplete?.completedAt ??
    planToComplete?.custom?.plannedAt ??
    now.toISOString();
  const foodTotals = computeFoodTotalsByMealPlan(body);
  const consumed = foodTotals.get(planId) ?? emptyTotals();
  const isEmpty =
    consumed.calories === 0 &&
    consumed.protein === 0 &&
    consumed.carbs === 0 &&
    consumed.fat === 0;

  // Completing locks the meal's target to what was actually consumed. That
  // becomes the effective value, so any prior custom target override is cleared.
  const lockedPlans: DiaryMeal[] = body.meals.map((plan) => {
    if (plan.id !== planId) return plan;
    const nextCustom = plan.custom ? { ...plan.custom } : undefined;
    if (nextCustom) delete nextCustom.target;
    return {
      ...plan,
      completedAt,
      skippedAt: isEmpty ? completedAt : null,
      planned: {
        ...plan.planned,
        target: {
          calories: Math.round(consumed.calories),
          protein: Math.round(consumed.protein),
          carbs: Math.round(consumed.carbs),
          fat: Math.round(consumed.fat),
        },
      },
      custom: nextCustom && Object.keys(nextCustom).length > 0 ? nextCustom : undefined,
    };
  });

  const result = recalculateMealPlans({
    body: { ...body, meals: lockedPlans },
    selectedDate: options.selectedDate,
    now,
  });

  // Always include the completed plan in changed IDs.
  const changed = Array.from(new Set([planId, ...result.changedPlanIds]));
  return {
    mealPlans: result.mealPlans,
    changedPlanIds: changed,
    message: isEmpty ? "Meal skipped" : "Meal completed. Targets recalculated.",
  };
};

/**
 * Uncomplete a plan: clear completedAt/skippedAt and unlock completion-set
 * lock flags. Does NOT trigger recalculation per spec.
 */
export const uncompletePlan = (
  body: DiaryDayBody,
  planId: string,
): DiaryMeal[] =>
  body.meals.map((plan) => {
    if (plan.id !== planId) return plan;
    return {
      ...plan,
      completedAt: null,
      skippedAt: null,
    };
  });

/** Build a synthetic UTC timestamp for a local date+time. Helper for tests. */
export const localToUtcIso = (date: string, time: string): string => {
  const [y, mo, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  return new Date(y, mo - 1, d, hh, mm, 0, 0).toISOString();
};

/** Wrapper used by quickly building a "now" instant in local timezone. */
export const localNowParts = (now: Date = new Date()) => ({
  date: formatDateInput(now),
  time: formatTimeInput(now),
});
